import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  TablePagination,
  Tooltip,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  InfoOutlined as InfoOutlinedIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { SirketLookup } from '../components/SirketLookup';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles, getRefreshTableStyles } from '../utils/refreshAnimation';
import { bankServiceRunService } from '../services/bankServiceRunService';
import { bankaService } from '../services/bankaService';
import { serverService } from '../services/serverService';
import { agentService } from '../services/agentService';
import type { BankServiceRun, SirketHesapDto } from '../types/bankServiceRun';
import type { Banka } from '../types/banka';
import type { Server } from '../types/server';
import type { Agent } from '../types/agent';
import { formatDateToTR } from '../utils/dateUtils';

const STATUS_LABELS: Record<number, string> = {
  0: 'Bekliyor',
  1: 'İşleniyor',
  2: 'Tamamlandı',
  3: 'Başarısız',
  4: 'İptal',
};

const STATUS_COLOR: Record<number, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  0: 'default',
  1: 'primary',
  2: 'success',
  3: 'error',
  4: 'warning',
};

const statusToNumber = (status: number | string): number => {
  if (typeof status === 'number') return status;
  const map: Record<string, number> = { Pending: 0, Processing: 1, Completed: 2, Failed: 3, Cancelled: 4 };
  return map[status] ?? 0;
};

const getStatusLabel = (status: number | string, fallback: string): string =>
  STATUS_LABELS[statusToNumber(status)] ?? fallback;

const getStatusColor = (status: number | string): 'default' | 'primary' | 'success' | 'error' | 'warning' =>
  STATUS_COLOR[statusToNumber(status)] ?? 'default';

const isPendingOrProcessing = (run: BankServiceRun): boolean =>
  run.status === 0 || run.status === 1 || run.status === 'Pending' || run.status === 'Processing';

interface RequestParams {
  bankCode?: number;
  serviceVkn?: string;
  uid?: string;
  logLevel?: number;
  beginDate?: string;
  endDate?: string;
  dataOnly?: boolean;
  inspect?: boolean;
  skipJobLog?: boolean;
  nextRunSkip?: boolean;
  manuelRun?: boolean;
  runWithoutDate?: boolean;
  requestInclude?: boolean;
}

const defaultFormState = {
  serverId: '',
  serviceVkn: '',
  bankCode: '',
  uid: '',
  beginDate: '',
  endDate: '',
  dataOnly: false,
  inspect: true,
  skipJobLog: true,
  nextRunSkip: true,
  manuelRun: false,
  runWithoutDate: false,
  requestResponseDownload: false,
  requestInclude: true,
};

/** datetime-local için şu anki tarih/saat: yyyy-MM-ddTHH:mm */
const getDateTimeLocalNow = (): string => {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  const h = String(n.getHours()).padStart(2, '0');
  const min = String(n.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

/** datetime-local için şu anki zamandan 1 gün sonrası: yyyy-MM-ddTHH:mm */
const getDateTimeLocalOneDayLater = (): string => {
  const n = new Date();
  n.setDate(n.getDate() + 1);
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  const h = String(n.getHours()).padStart(2, '0');
  const min = String(n.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

/** CLI tarih formatı: -b / -e için eski uygulama formatı yyyy/MM/ddTHH:mm (RunServicev2doc.md). */
const toCliDate = (dateStr: string | undefined): string => {
  if (!dateStr || !dateStr.trim()) return '';
  const s = dateStr.trim();
  const [datePart, timePart] = s.split('T');
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return s;
  const t = timePart && /^\d{1,2}:\d{2}/.test(timePart) ? timePart.slice(0, 5) : '00:00';
  return `${y}/${m}/${d}T${t}`;
};

/**
 * Request parametrelerinden runservice.cmd içinde çalışacak komut satırını oluşturur.
 * RunServicev2doc.md CLIOptions.getParamaters ile birebir: -r -l{LogLevel} -v -c -g (uid) -b -e, -k 1, -n yok.
 */
const buildRequestCommandLine = (requestParametersJson: string | undefined): string => {
  if (!requestParametersJson || !requestParametersJson.trim()) return '';
  try {
    const p = JSON.parse(requestParametersJson) as RequestParams;
    const logLevel = p.logLevel ?? 0;
    const parts: string[] = ['dotnet estatement_service.dll', '-r', `-l${logLevel}`];
    if (p.serviceVkn) parts.push(`-v${p.serviceVkn}`);
    if (p.bankCode !== undefined) parts.push(`-c${p.bankCode}`);
    if (p.uid) parts.push(`-g${p.uid}`);
    if (!p.runWithoutDate) {
      const b = toCliDate(p.beginDate);
      const e = toCliDate(p.endDate);
      if (b) parts.push(`-b${b}`);
      if (e) parts.push(`-e${e}`);
    }
    if (p.inspect) parts.push('-i');
    if (p.dataOnly) parts.push('-d');
    if (p.skipJobLog) parts.push('-s');
    if (p.nextRunSkip) parts.push('-a');
    if (p.manuelRun) parts.push('-k 1');
    if (p.requestInclude) parts.push('-w***');
    return parts.join(' ');
  } catch {
    return requestParametersJson;
  }
};

const consoleBoxSx = {
  bgcolor: '#1e1e1e',
  color: '#d4d4d4',
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: '0.813rem',
  p: 2,
  borderRadius: 1,
  overflow: 'auto',
  maxHeight: 320,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  border: '1px solid #333',
};

const formPreviewBoxSx = {
  ...consoleBoxSx,
  maxHeight: 140,
  fontSize: '0.75rem',
  p: 1.5,
};

const CHECKBOX_OPTIONS: Array<{
  key: keyof typeof defaultFormState;
  label: string;
  tooltip: string;
}> = [
  { key: 'runWithoutDate', label: 'Tarih kullanma', tooltip: 'İşaretlenirse başlangıç/bitiş tarihi kullanılmaz; servis -n (nodate) ile çalışır.' },
  { key: 'dataOnly', label: 'Sadece veri', tooltip: 'Sadece veri çekilir; (-d).' },
  { key: 'inspect', label: 'Denetleme', tooltip: 'Denetleme (inspect) modunda çalıştırır (-i).' },
  { key: 'skipJobLog', label: 'İş günlüğünü atla', tooltip: 'İş günlüğü kaydı yazılmaz (-s).' },
  { key: 'nextRunSkip', label: 'Çalışma zamanını atla', tooltip: 'Bir sonraki çalışma zamanı kontrolü atlanır (-a).' },
  { key: 'manuelRun', label: 'Manuel çalıştırma', tooltip: 'Manuel tetikleme olarak işaretlenir (-k).' },
  { key: 'requestInclude', label: "Request'i dahil et", tooltip: "Aktif ise agent konfigürasyonundaki Run Service Admin Passwd -w parametresi ile gönderilir." },
];

const getDetailResponseFullText = (run: BankServiceRun | null): string => {
  if (!run) return '';
  if (run.errorMessage)
    return `Error:\n${run.errorMessage}${run.responseBody ? `\n\n${run.responseBody}` : ''}`;
  return run.responseBody || '(yanıt veya hata mesajı yok)';
};

type ResponseSegment = { type: 'text'; value: string } | { type: 'match'; value: string; index: number };

const getResponseSegments = (text: string, search: string): ResponseSegment[] => {
  if (!search.trim()) return [{ type: 'text', value: text }];
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const segments: ResponseSegment[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex)
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    segments.push({ type: 'match', value: m[1], index: matchIndex++ });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) segments.push({ type: 'text', value: text.slice(lastIndex) });
  return segments;
};

const getResponseMatchCount = (text: string, search: string): number => {
  if (!search.trim()) return 0;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escaped, 'gi');
  let count = 0;
  while (regex.exec(text) !== null) count++;
  return count;
};

interface FromSonIslemZamaniState {
  serverId: string;
  serviceVkn: string;
  bankCode: string;
  uid: string;
  beginDate?: string;
  endDate?: string;
}

const BankaServisTetikleme = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedServerIds, refreshTrigger, isRefreshing } = useFilter();
  const [servers, setServers] = useState<Server[]>([]);
  const [bankalar, setBankalar] = useState<Banka[]>([]);
  const [sirketHesaplar, setSirketHesaplar] = useState<SirketHesapDto[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [retryRun, setRetryRun] = useState<BankServiceRun | null>(null);
  const [form, setForm] = useState(defaultFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [runs, setRuns] = useState<BankServiceRun[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | ''>('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailFullScreen, setDetailFullScreen] = useState(false);
  const [detailRun, setDetailRun] = useState<BankServiceRun | null>(null);
  const [responseSearchText, setResponseSearchText] = useState('');
  const [responseMatchIndex, setResponseMatchIndex] = useState(0);
  const responseContainerRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [runToDelete, setRunToDelete] = useState<BankServiceRun | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tick, setTick] = useState(0);
  const [justUpdatedRunIds, setJustUpdatedRunIds] = useState<Set<string>>(new Set());
  const scheduledRefetchTimeoutsRef = useRef<Map<string, number>>(new Map());
  const virtualLastCheckByServerIdRef = useRef<Record<string, number>>({});
  const previousRunsRef = useRef<BankServiceRun[] | null>(null);
  const justUpdatedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reachableServerIds = useMemo(
    () => new Set(agents.filter((a) => a.isOnline).map((a) => a.serverId)),
    [agents]
  );

  const nowMs = useMemo(() => Date.now(), [tick]);
  const getCountdown = useCallback(
    (run: BankServiceRun) => {
      const runServerIdLower = run.serverId?.toLowerCase();
      const agent = agents.find((a) => a.serverId?.toLowerCase() === runServerIdLower);
      const intervalSec = agent?.bankServiceRunIntervalSeconds ?? 60;
      let lastMs: number;
      if (agent?.lastBankServiceRunCheckAt) {
        lastMs = new Date(agent.lastBankServiceRunCheckAt).getTime();
        delete virtualLastCheckByServerIdRef.current[run.serverId];
      } else {
        virtualLastCheckByServerIdRef.current[run.serverId] ??= nowMs;
        lastMs = virtualLastCheckByServerIdRef.current[run.serverId];
      }
      const nextCheckMs = lastMs + intervalSec * 1000;
      const remainingMs = nextCheckMs - nowMs;
      return {
        secondsRemaining: Math.floor(remainingMs / 1000),
        isPast: remainingMs <= 0,
      };
    },
    [agents, nowMs]
  );

  const formatCountdown = (secondsRemaining: number): string => {
    if (secondsRemaining >= 60) {
      const m = Math.floor(secondsRemaining / 60);
      const s = secondsRemaining % 60;
      return `${m} dk ${s} sn`;
    }
    return `${secondsRemaining} sn`;
  };

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (detailRun) {
      setResponseSearchText('');
      setResponseMatchIndex(0);
    }
  }, [detailRun]);

  const detailResponseFullText = getDetailResponseFullText(detailRun);
  const responseMatchCount = getResponseMatchCount(detailResponseFullText, responseSearchText);
  const responseSegments = getResponseSegments(detailResponseFullText, responseSearchText);
  const clampedMatchIndex = responseMatchCount === 0 ? 0 : Math.min(responseMatchIndex, responseMatchCount - 1);

  useEffect(() => {
    if (responseMatchCount === 0 || !responseContainerRef.current) return;
    const el = responseContainerRef.current.querySelector(`[data-match-index="${clampedMatchIndex}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [clampedMatchIndex, responseMatchCount]);

  const handleCopyResponse = useCallback(() => {
    const text = getDetailResponseFullText(detailRun);
    if (text) void navigator.clipboard.writeText(text);
  }, [detailRun]);

  const handleResponsePrevMatch = () => {
    if (responseMatchCount === 0) return;
    setResponseMatchIndex((prev) => (prev - 1 + responseMatchCount) % responseMatchCount);
  };

  const handleResponseNextMatch = () => {
    if (responseMatchCount === 0) return;
    setResponseMatchIndex((prev) => (prev + 1) % responseMatchCount);
  };

  const loadServers = useCallback(async () => {
    try {
      const data = await serverService.getAll();
      setServers(data);
    } catch {
      setListError('Sunucular yüklenemedi');
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const data = await agentService.getAll();
      setAgents(data);
    } catch {
      setAgents([]);
    }
  }, []);

  useEffect(() => {
    const timeouts = scheduledRefetchTimeoutsRef.current;
    runs.forEach((run) => {
      if (!isPendingOrProcessing(run)) return;
      const { secondsRemaining, isPast } = getCountdown(run);
      if (!isPast || secondsRemaining === null) return;
      if (timeouts.has(run.id)) return;
      const timeoutId = window.setTimeout(() => {
        bankServiceRunService
          .getById(run.id)
          .then((updated) => {
            const merged = {
              ...updated,
              sirketUnvani: updated.sirketUnvani ?? run.sirketUnvani,
              bankaAdi: updated.bankaAdi ?? run.bankaAdi,
            };
            setRuns((prev) => prev.map((r) => (r.id === run.id ? merged : r)));
            setJustUpdatedRunIds((s) => {
              const next = new Set(s);
              next.add(run.id);
              return next;
            });
            if (justUpdatedTimeoutRef.current) clearTimeout(justUpdatedTimeoutRef.current);
            justUpdatedTimeoutRef.current = setTimeout(() => {
              setJustUpdatedRunIds((prevSet) => {
                const n = new Set(prevSet);
                n.delete(run.id);
                return n;
              });
              justUpdatedTimeoutRef.current = null;
            }, 650);
            if (detailRun?.id === run.id) setDetailRun(merged);
            loadAgents();
            virtualLastCheckByServerIdRef.current[updated.serverId] = Date.now();
          })
          .finally(() => {
            timeouts.delete(run.id);
          });
      }, 5000);
      timeouts.set(run.id, timeoutId);
    });
  }, [runs, getCountdown, detailRun?.id, loadAgents]);

  useEffect(() => {
    return () => {
      scheduledRefetchTimeoutsRef.current.forEach((id) => clearTimeout(id));
      scheduledRefetchTimeoutsRef.current.clear();
    };
  }, []);

  const loadBankalar = useCallback(async () => {
    try {
      const data = await bankaService.getAll();
      setBankalar(data);
    } catch {
      setBankalar([]);
    }
  }, []);

  const loadSirketHesaplar = useCallback(async (serverId: string, vkn?: string, bankaKodu?: number) => {
    if (!serverId) return;
    try {
      const data = await bankServiceRunService.getSirketHesaplar(
        serverId,
        vkn || undefined,
        bankaKodu
      );
      setSirketHesaplar(data);
    } catch {
      setSirketHesaplar([]);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const listServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
      const result = await bankServiceRunService.getList({
        serverId: listServerId,
        status: filterStatus === '' ? undefined : filterStatus,
        page: page + 1,
        pageSize,
      });
      const prev = previousRunsRef.current;
      if (prev !== null && result.items.length > 0) {
        const prevMap = new Map(prev.map((r) => [r.id, r]));
        const updatedIds = result.items
          .filter((r) => {
            const p = prevMap.get(r.id);
            if (!p) return false;
            return p.status !== r.status || p.startedAt !== r.startedAt || p.completedAt !== r.completedAt;
          })
          .map((r) => r.id);
        if (updatedIds.length > 0) {
          setJustUpdatedRunIds((s) => new Set([...Array.from(s), ...updatedIds]));
          if (justUpdatedTimeoutRef.current) clearTimeout(justUpdatedTimeoutRef.current);
          justUpdatedTimeoutRef.current = setTimeout(() => {
            setJustUpdatedRunIds(new Set());
            justUpdatedTimeoutRef.current = null;
          }, 650);
        }
      }
      previousRunsRef.current = result.items;
      setRuns(result.items);
      setTotalCount(result.totalCount);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      setListError(message || 'Liste yüklenirken hata oluştu');
    } finally {
      setListLoading(false);
    }
  }, [selectedServerIds, filterStatus, page, pageSize]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents, refreshTrigger]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns, refreshTrigger]);

  useEffect(() => {
    loadBankalar();
  }, [loadBankalar]);

  useEffect(() => {
    const state = location.state as { fromSonIslemZamani?: FromSonIslemZamaniState } | null;
    const from = state?.fromSonIslemZamani;
    if (!from || servers.length === 0) return;
    setForm({
      ...defaultFormState,
      serverId: from.serverId,
      serviceVkn: from.serviceVkn ?? '',
      bankCode: from.bankCode ?? '',
      uid: from.uid ?? '',
      beginDate: from.beginDate ?? getDateTimeLocalNow(),
      endDate: from.endDate ?? getDateTimeLocalNow(),
      runWithoutDate: !from.beginDate && !from.endDate,
    });
    setRetryRun(null);
    setFormError(null);
    setFormOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, servers.length, navigate]);

  useEffect(() => {
    if (form.serverId && (form.serviceVkn || form.bankCode !== '')) {
      loadSirketHesaplar(
        form.serverId,
        form.serviceVkn || undefined,
        form.bankCode !== '' ? Number(form.bankCode) : undefined
      );
    } else {
      setSirketHesaplar([]);
    }
  }, [form.serverId, form.serviceVkn, form.bankCode, loadSirketHesaplar]);

  useEffect(() => {
    if (!form.serverId) {
      setSirketHesaplar([]);
    }
  }, [form.serverId]);

  const buildRequestParameters = (): string => {
    const params: RequestParams = {
      bankCode: form.bankCode !== '' ? Number(form.bankCode) : undefined,
      serviceVkn: form.serviceVkn || undefined,
      uid: form.uid || undefined,
      dataOnly: form.dataOnly,
      inspect: form.inspect,
      skipJobLog: form.skipJobLog,
      nextRunSkip: form.nextRunSkip,
      manuelRun: form.manuelRun,
      runWithoutDate: form.runWithoutDate,
      requestInclude: form.requestInclude,
    };
    if (!form.runWithoutDate) {
      if (form.beginDate) params.beginDate = form.beginDate;
      if (form.endDate) params.endDate = form.endDate;
    }
    return JSON.stringify(params);
  };

  const handleOpenForm = (run?: BankServiceRun) => {
    setRetryRun(run || null);
    if (run && run.requestParameters) {
      try {
        const p = JSON.parse(run.requestParameters) as RequestParams;
        setForm({
          serverId: run.serverId,
          serviceVkn: p.serviceVkn ?? '',
          bankCode: p.bankCode !== undefined ? String(p.bankCode) : '',
          uid: p.uid ?? '',
          beginDate: p.beginDate ?? '',
          endDate: p.endDate ?? '',
          dataOnly: p.dataOnly ?? false,
          inspect: p.inspect ?? false,
          skipJobLog: p.skipJobLog ?? false,
          nextRunSkip: p.nextRunSkip ?? false,
          manuelRun: p.manuelRun ?? false,
          runWithoutDate: p.runWithoutDate ?? false,
          requestResponseDownload: run.requestResponseDownload ?? false,
          requestInclude: p.requestInclude ?? true,
        });
      } catch {
        setForm(defaultFormState);
      }
    } else {
      setForm({
        ...defaultFormState,
        serverId: selectedServerIds.length === 1 ? selectedServerIds[0] : (servers[0]?.id ?? ''),
        beginDate: getDateTimeLocalNow(),
        endDate: getDateTimeLocalOneDayLater(),
      });
    }
    setFormError(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setRetryRun(null);
    setForm(defaultFormState);
  };

  const handleSubmitForm = async () => {
    if (!form.serverId) {
      setFormError('Sunucu seçiniz.');
      return;
    }
    setSubmitLoading(true);
    setFormError(null);
    try {
      if (retryRun) {
        await bankServiceRunService.retry(
          retryRun.id,
          buildRequestParameters(),
          form.requestResponseDownload
        );
      } else {
        await bankServiceRunService.create({
          serverId: form.serverId,
          requestParameters: buildRequestParameters(),
          requestResponseDownload: form.requestResponseDownload,
        });
      }
      handleCloseForm();
      loadRuns();
    } catch (err: unknown) {
      const res = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: { message?: string } } }).response
        : null;
      const message = res?.data?.message ?? null;
      setFormError(message || 'İstek gönderilirken hata oluştu');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRetryClick = (run: BankServiceRun) => {
    handleOpenForm(run);
  };

  const handleDeleteClick = (run: BankServiceRun) => {
    setRunToDelete(run);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!runToDelete) return;
    try {
      await bankServiceRunService.delete(runToDelete.id);
      setDeleteDialogOpen(false);
      setRunToDelete(null);
      loadRuns();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      alert(message || 'Silinirken hata oluştu');
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h4" component="h1">
            Banka Servis Tetikleme
          </Typography>
          <Tooltip title={reachableServerIds.size === 0 ? 'Sunucuya erişilemiyor (online agent yok)' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              disabled={reachableServerIds.size === 0}
            >
              Yeni Tetikleme
            </Button>
          </span>
        </Tooltip>
        </Box>

        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="filter-status-label">Durum</InputLabel>
            <Select
              labelId="filter-status-label"
              label="Durum"
              value={filterStatus}
              onChange={(e) => {
                const v = e.target.value as string | number;
                setFilterStatus(v === '' || v === undefined ? '' : Number(v));
              }}
            >
              <MenuItem value="">Tümü</MenuItem>
              {[0, 1, 2, 3, 4].map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {listError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {listError}
          </Alert>
        )}

        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {listLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table size="small" sx={getRefreshTableStyles(isRefreshing)}>
                    <TableHead>
                    <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 150 }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Sunucu</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Şirket</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Banka</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>İstek Tarihi</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Başlangıç</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Bitiş</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          Kayıt bulunamadı.
                        </TableCell>
                      </TableRow>
                    ) : (
                      runs.map((run, index) => {
                        let beginDate = '';
                        let endDate = '';
                        try {
                          const p = JSON.parse(run.requestParameters || '{}') as RequestParams;
                          beginDate = p.beginDate ?? '';
                          endDate = p.endDate ?? '';
                        } catch {}
                        const isJustUpdated = justUpdatedRunIds.has(run.id);
                        return (
                          <TableRow
                            key={run.id}
                            hover
                            sx={{
                              bgcolor: isJustUpdated ? 'rgba(16, 185, 129, 0.22)' : index % 2 === 1 ? 'action.hover' : 'transparent',
                              transition: 'background-color 0.5s ease-out',
                              '&:hover': { bgcolor: 'action.selected' },
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 150 }}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={getStatusLabel(run.status, run.statusName)}
                                  size="small"
                                  sx={{ height: 22, fontSize: '0.75rem' }}
                                  color={getStatusColor(run.status)}
                                />
                                {isPendingOrProcessing(run) ? (() => {
                                  const { secondsRemaining, isPast } = getCountdown(run);
                                  if (secondsRemaining === null) return null;
                                  if (isPast) return <Typography component="span" variant="body2" color="primary" sx={{ ml: 0.5 }}>(Güncelleniyor 5 sn)</Typography>;
                                  return <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', ml: 0.5 }}>({formatCountdown(Math.max(0, secondsRemaining))})</Typography>;
                                })() : null}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{run.serverName}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{run.sirketUnvani ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{run.bankaAdi ?? '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{formatDateToTR(run.requestedAt)}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{beginDate || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{endDate || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                              <Tooltip title="Detay / Yanıt">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDetailRun(run);
                                    setDetailDialogOpen(true);
                                  }}
                                  aria-label="Detay"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  reachableServerIds.has(run.serverId)
                                    ? 'Yeniden tetikle (parametreleri düzenleyip mevcut kaydı günceller)'
                                    : 'Sunucuya erişilemiyor (online agent yok)'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleRetryClick(run)}
                                    disabled={!reachableServerIds.has(run.serverId)}
                                    aria-label="Yeniden tetikle"
                                  >
                                    <ReplayIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(run)}
                                  aria-label="Sil"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Sayfa başına:"
              />
              </>
            )}
          </TableContainer>
        </Paper>
      </Box>

      {/* Form / Retry Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>{retryRun ? 'Tekrar Çalıştır' : 'Yeni Tetikleme'}</DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1, '& .MuiAutocomplete-root': { minWidth: 0 } }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {formError}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              mb: 1.5,
              mt: 0.5,
            }}
          >
            <Autocomplete
              size="small"
              options={servers}
              value={servers.find((s) => s.id === form.serverId) ?? null}
              getOptionLabel={(s) => s.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, newValue) =>
                setForm((prev) => ({
                  ...prev,
                  serverId: newValue?.id ?? '',
                  serviceVkn: '',
                  bankCode: '',
                  uid: '',
                }))
              }
              renderInput={(params) => <TextField {...params} label="Sunucu" placeholder="Sunucu ara..." />}
              noOptionsText="Sunucu yok"
              sx={{ width: '100%' }}
            />
            <SirketLookup
              value={form.serviceVkn}
              onChange={(vkn) =>
                setForm((prev) => ({
                  ...prev,
                  serviceVkn: vkn,
                  bankCode: '',
                  uid: '',
                }))
              }
              serverId={form.serverId || undefined}
              label="Şirket (VKN)"
              placeholder={form.serverId ? 'Şirket ara...' : 'Önce sunucu seçiniz'}
              size="small"
              disabled={!form.serverId}
              sx={{ width: '100%' }}
            />
            <Autocomplete
              size="small"
              options={sirketHesaplar}
              value={sirketHesaplar.find((h) => h.uid === form.uid) ?? null}
              getOptionLabel={(h) => h.uid}
              isOptionEqualToValue={(a, b) => a.uid === b.uid}
              onChange={(_, newValue) => setForm((prev) => ({ ...prev, uid: newValue?.uid ?? '' }))}
              disabled={!form.serviceVkn || !form.bankCode}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="UID"
                  placeholder={form.serviceVkn && form.bankCode ? 'UID ara...' : 'Önce şirket ve banka seçiniz'}
                />
              )}
              noOptionsText="Hesap yok"
              sx={{ width: '100%' }}
            />
            {(() => {
              const bankaKodlari = Array.from(new Set(sirketHesaplar.map((h) => h.bankaKodu)));
              const filtrelenmisBankalar = bankalar.filter((b) => bankaKodlari.includes(b.bankaKodu));
              return (
                <Autocomplete
                  size="small"
                  options={filtrelenmisBankalar}
                  value={filtrelenmisBankalar.find((b) => String(b.bankaKodu) === form.bankCode) ?? null}
                  getOptionLabel={(b) => `${b.bankaAdi} (${b.bankaKodu})`}
                  isOptionEqualToValue={(a, b) => a.bankaKodu === b.bankaKodu}
                  onChange={(_, newValue) =>
                    setForm((prev) => ({ ...prev, bankCode: newValue ? String(newValue.bankaKodu) : '', uid: '' }))
                  }
                  disabled={!form.serviceVkn}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Banka"
                      placeholder={form.serviceVkn ? 'Banka ara...' : 'Önce şirket seçiniz'}
                    />
                  )}
                  noOptionsText="Banka yok"
                  sx={{ width: '100%' }}
                />
              );
            })()}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.runWithoutDate}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const nowStr = getDateTimeLocalNow();
                  setForm((prev) => ({
                    ...prev,
                    runWithoutDate: checked,
                    ...(checked ? { beginDate: nowStr, endDate: nowStr } : {}),
                  }));
                }}
              />
            }
            label={
              <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                {CHECKBOX_OPTIONS.find((o) => o.key === 'runWithoutDate')?.label}
                <Tooltip title={CHECKBOX_OPTIONS.find((o) => o.key === 'runWithoutDate')?.tooltip}>
                  <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
            sx={{ mb: 1 }}
          />
          {!form.runWithoutDate && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Başlangıç"
                type="datetime-local"
                value={form.beginDate}
                onChange={(e) => setForm((prev) => ({ ...prev, beginDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                size="small"
                label="Bitiş"
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 0.5,
              mb: 1,
            }}
          >
            {CHECKBOX_OPTIONS.filter((o) => o.key !== 'runWithoutDate').map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(form[opt.key])}
                    onChange={(e) => setForm((prev) => ({ ...prev, [opt.key]: e.target.checked }))}
                  />
                }
                label={
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    {opt.label}
                    <Tooltip title={opt.tooltip}>
                      <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            ))}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={form.requestResponseDownload}
                onChange={(e) => setForm((prev) => ({ ...prev, requestResponseDownload: e.target.checked }))}
              />
            }
            label={
              <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                Request/Response indir
                <Tooltip title="İşlem tamamlandığında istek ve yanıt bir dosya olarak kaydedilir; İstek Detayı ekranından indirilebilir.">
                  <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label="Açıklama">
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
            sx={{ mb: 1.5 }}
          />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
            Request önizlemesi
          </Typography>
          <Box sx={formPreviewBoxSx} component="pre">
            {buildRequestCommandLine(buildRequestParameters()) || '(Parametreleri doldurun)'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleSubmitForm}
            disabled={submitLoading || !form.serverId || !reachableServerIds.has(form.serverId)}
          >
            {submitLoading ? 'Gönderiliyor...' : retryRun ? 'Tekrar Çalıştır' : 'Tetikle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailFullScreen(false);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={detailFullScreen}
        sx={
          detailFullScreen
            ? {
                '& .MuiDialog-paper': {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  maxHeight: '100vh',
                },
              }
            : undefined
        }
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>İstek Detayı</span>
          <Box display="flex" alignItems="center" gap={0.5}>
            {detailRun?.hasRequestResponseFile && (
              <Tooltip title="Request/Response dosyasını indir">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => detailRun && void bankServiceRunService.downloadRequestResponse(detailRun.id)}
                  aria-label="Request/Response indir"
                >
                  İndir
                </Button>
              </Tooltip>
            )}
            <Tooltip title={detailFullScreen ? 'Tam ekrandan çık' : 'Tam ekran'}>
              <IconButton
                size="small"
                onClick={() => setDetailFullScreen((prev) => !prev)}
                aria-label={detailFullScreen ? 'Tam ekrandan çık' : 'Tam ekran'}
              >
                {detailFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={
            detailFullScreen
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  flex: 1,
                  minHeight: 0,
                }
              : undefined
          }
        >
          {detailRun && (
            <Box
              sx={{
                pt: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                ...(detailFullScreen ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
              }}
            >
              <Box sx={detailFullScreen ? { flexShrink: 0 } : {}}>
                <Typography variant="subtitle2" color="text.secondary">
                  Durum
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {getStatusLabel(detailRun.status, detailRun.statusName)}
                </Typography>
              </Box>

              <Box sx={detailFullScreen ? { flexShrink: 0 } : {}}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Request
                </Typography>
                <Box sx={consoleBoxSx} component="pre">
                  {buildRequestCommandLine(detailRun.requestParameters) || '(parametre yok)'}
                </Box>
              </Box>

              <Box
                sx={{
                  ...(detailFullScreen ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : {}),
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Response
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                  <TextField
                    size="small"
                    placeholder="Response içinde ara…"
                    value={responseSearchText}
                    onChange={(e) => {
                      setResponseSearchText(e.target.value);
                      setResponseMatchIndex(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 220 }}
                    aria-label="Response filtre"
                  />
                  {responseSearchText.trim() && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {responseMatchCount === 0
                          ? 'Eşleşme yok'
                          : `${clampedMatchIndex + 1} / ${responseMatchCount} eşleşme`}
                      </Typography>
                      <Tooltip title="Önceki eşleşme">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleResponsePrevMatch}
                            disabled={responseMatchCount === 0}
                            aria-label="Önceki eşleşme"
                          >
                            <NavigateBeforeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Sonraki eşleşme">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleResponseNextMatch}
                            disabled={responseMatchCount === 0}
                            aria-label="Sonraki eşleşme"
                          >
                            <NavigateNextIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Box sx={{ flex: 1, minWidth: 8 }} />
                  <Tooltip title="Yanıtı kopyala">
                    <IconButton
                      size="small"
                      onClick={handleCopyResponse}
                      aria-label="Yanıtı kopyala"
                      sx={{ bgcolor: 'action.hover' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  ref={responseContainerRef}
                  sx={{
                    ...consoleBoxSx,
                    ...(detailFullScreen
                      ? { flex: 1, minHeight: 0, overflow: 'auto', maxHeight: 'none' }
                      : { maxHeight: 320 }),
                  }}
                  component="pre"
                >
                  {responseSegments.map((seg, i) =>
                    seg.type === 'text' ? (
                      <span key={i}>{seg.value}</span>
                    ) : (
                      <mark
                        key={i}
                        data-match-index={seg.index}
                        style={{
                          backgroundColor: seg.index === clampedMatchIndex ? '#b38600' : '#5c4d00',
                          color: '#1e1e1e',
                          padding: 0,
                          borderRadius: 2,
                        }}
                      >
                        {seg.value}
                      </mark>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Kaydı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu tetikleme kaydını silmek istediğinize emin misiniz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default BankaServisTetikleme;
